/**
 * periodLockToast.js — iter1234
 *
 * Unified reactive helper for "the server rejected this action because a
 * closed accounting period is in the way". Surfaces the exact message
 * from the API and gives the user a ONE-CLICK deep-link to the Period
 * Close screen (with the blocking period pre-selected when we can parse
 * it out of the detail string).
 *
 * Usage:
 *   import { showPeriodLockToast } from '@/utils/periodLockToast';
 *   ...
 *   try { await axios.delete(...) }
 *   catch (err) {
 *     if (!showPeriodLockToast(err, navigate)) {
 *       toast.error(err.response?.data?.detail || 'Something went wrong');
 *     }
 *   }
 *
 * Returns:
 *   true  → error was a period-lock error and has been shown to the user
 *   false → not a period-lock error, caller should show its own toast
 */
import { toast } from 'sonner';

// Anything that plausibly means "a closed / locked accounting period is
// blocking this write". We match against the server's `detail` string so
// any endpoint using `check_period_lock`, `check_journals_delete_lock`,
// or the new iter1234 statement-delete guard is covered without any
// per-endpoint wiring.
const PERIOD_LOCK_REGEXES = [
  /locked period/i,
  /closed period/i,
  /period.*locked/i,
  /period.*closed/i,
  /reopen.*period/i,
  /unlock.*period/i,
  /monthly-locked/i,
  /annual-locked/i,
  /before deleting/i,
  /before you can/i, // "you must reopen X before you can ..."
];

// YYYY-MM anywhere in the detail. Multiple hits → we use the earliest one
// for the deep-link so the user lands on the period they most likely need
// to open first.
const PERIOD_RE = /\b(\d{4}-\d{2})\b/g;

function extractDetail(err) {
  if (!err) return '';
  if (typeof err === 'string') return err;
  const data = err?.response?.data;
  if (!data) return err?.message || '';
  const d = data.detail;
  if (typeof d === 'string') return d;
  // Some endpoints return { detail: { error, message, ... } }
  if (d && typeof d === 'object') return d.message || d.error || JSON.stringify(d);
  if (typeof data === 'string') return data;
  return data.message || data.error || err?.message || '';
}

function looksLikePeriodLock(detail, status) {
  if (!detail) return false;
  // Phrase match is authoritative — the server's `detail` string always
  // includes one of these markers when a period lock is the reason. We
  // don't gate on status code because callers sometimes pass a plain
  // string (no status available). `status` is kept in the signature for
  // future signal-boosting but is intentionally unused today.
  void status;
  return PERIOD_LOCK_REGEXES.some((re) => re.test(detail));
}

function extractEarliestPeriod(detail) {
  if (!detail) return null;
  const matches = detail.match(PERIOD_RE);
  if (!matches || !matches.length) return null;
  // Deduplicate then sort ascending; earliest period is the one the user
  // must reopen first (cumulative lock semantics).
  return [...new Set(matches)].sort()[0];
}

/**
 * Try to render a period-lock toast for the given error.
 *
 * @param {*} err       — axios error, thrown Error, or plain detail string.
 * @param {Function} navigate — react-router `useNavigate()` result.
 * @param {Object} [opts]
 * @param {string} [opts.fallbackPeriod] — YYYY-MM used when the message
 *   itself contains no period reference (e.g. the caller already knows
 *   which date they were trying to post).
 * @param {number} [opts.duration=10000] — toast duration in ms.
 * @param {string} [opts.id] — sonner toast id, useful to dedupe repeated
 *   attempts of the same action.
 * @returns {boolean} true when a period-lock toast was shown, else false.
 */
export function showPeriodLockToast(err, navigate, opts = {}) {
  const status = err?.response?.status;
  const detail = extractDetail(err);
  if (!looksLikePeriodLock(detail, status)) return false;

  const period = extractEarliestPeriod(detail) || opts.fallbackPeriod || null;
  const target = period ? `/period-close/${period}` : '/period-close';

  toast.error(detail, {
    id: opts.id,
    duration: opts.duration ?? 10000,
    action: {
      label: 'Open Period Close',
      onClick: () => {
        try {
          if (typeof navigate === 'function') navigate(target);
          else if (typeof window !== 'undefined') window.location.assign(target);
        } catch {
          if (typeof window !== 'undefined') window.location.assign(target);
        }
      },
    },
  });
  return true;
}

/**
 * Convenience wrapper: try period-lock first, fall through to a plain
 * `toast.error(fallbackMessage)` when the error isn't a period-lock.
 */
export function toastPeriodLockOr(err, navigate, fallbackMessage, opts = {}) {
  if (showPeriodLockToast(err, navigate, opts)) return;
  toast.error(fallbackMessage || extractDetail(err) || 'Something went wrong', {
    id: opts.id,
  });
}
